import type { CSSProperties } from 'react'

import type {
  ProfileBoardCell,
  ProfileFullGame,
  ProfileMove,
} from '@runtime/features/profile/model/profileTypes'

export type HistoryReviewData = {
  boardToShow: ProfileBoardCell[][] | null
  grid: number
  isPuzzleGame: boolean
  lastMove: ProfileMove | null
  meta: Record<string, unknown>
  moves: ProfileMove[]
  movesCount: number
  p1Color: string
  p1Name: string | null
  p2Color: string
  p2Name: string | null
  puzzleClicks: number | null
  puzzleTimeMs: number | null
  score1: number | null
  score2: number | null
}

const hasDetails = (game: ProfileFullGame | null) =>
  !!(game && game.board && Array.isArray(game.board) && game.board.length)

export function buildBoardForReview(
  game: ProfileFullGame | null,
  step: number,
) {
  if (!game || !hasDetails(game)) return null

  const finalBoard = game.board
  if (!finalBoard) return null
  const grid = game.grid || finalBoard.length || 0
  if (!grid) return null

  const isPuzzle =
    game.kind === 'puzzle' ||
    (game.meta &&
      typeof game.meta.mode === 'string' &&
      game.meta.mode.indexOf('puzzle') === 0)

  const moves: ProfileMove[] = Array.isArray(game.moves) ? game.moves : []
  const hasMoves = moves.length > 0

  if (isPuzzle && !hasMoves && finalBoard) {
    return finalBoard.map((row) =>
      row.map((cell) =>
        cell
          ? {
              key: cell.key,
              rot: cell.rot || 0,
            }
          : null,
      ),
    )
  }

  const board: ProfileBoardCell[][] = Array.from({ length: grid }, () =>
    Array(grid).fill(null),
  )

  const mid = grid >> 1
  if (finalBoard[mid] && finalBoard[mid][mid]) {
    const center = finalBoard[mid][mid]
    board[mid][mid] = {
      key: center.key,
      rot: center.rot || 0,
    }
  }

  const limit = Math.max(0, Math.min(step, moves.length))
  for (let index = 0; index < limit; index += 1) {
    const move = moves[index]
    if (!move) continue
    const { r, c, key } = move
    const rot = move.rot || 0
    if (typeof r !== 'number' || typeof c !== 'number' || !key) continue
    if (r < 0 || r >= grid || c < 0 || c >= grid) continue
    board[r][c] = { key, rot }
  }

  return board
}

export function buildHistoryReviewData(
  selectedGame: ProfileFullGame | null,
  reviewIndex: number,
): HistoryReviewData {
  const moves: ProfileMove[] = Array.isArray(selectedGame?.moves)
    ? selectedGame.moves
    : []
  const movesCount = moves.length
  const boardToShow = buildBoardForReview(selectedGame, reviewIndex)
  const grid = selectedGame?.grid || (boardToShow ? boardToShow.length : 0) || 0
  const lastMove =
    reviewIndex > 0 && reviewIndex <= movesCount ? moves[reviewIndex - 1] : null

  const players = Array.isArray(selectedGame?.players)
    ? selectedGame.players
    : []
  const p1 = players[0] || null
  const p2 = players[1] || null
  const p1Name = p1?.name || null
  const p2Name = p2?.name || null

  const meta = (selectedGame?.meta || {}) as Record<string, unknown>
  const p1Colors = Array.isArray(meta.p1Colors) ? meta.p1Colors : []
  const p2Colors = Array.isArray(meta.p2Colors) ? meta.p2Colors : []
  const p1Color = String(p1Colors[0] || p1?.color || 'RED')
  const p2Color = String(p2Colors[0] || p2?.color || 'BLU')

  let score1: number | null = null
  let score2: number | null = null
  if (movesCount && moves[0] && typeof moves[0].score1 === 'number') {
    if (reviewIndex === 0) {
      score1 = 0
      score2 = 0
    } else if (
      lastMove &&
      typeof lastMove.score1 === 'number' &&
      typeof lastMove.score2 === 'number'
    ) {
      score1 = lastMove.score1
      score2 = lastMove.score2
    }
  }

  const isPuzzleGame =
    selectedGame?.kind === 'puzzle' ||
    (typeof meta.mode === 'string' && meta.mode.indexOf('puzzle') === 0)

  const puzzleTimeMs =
    isPuzzleGame && p1 && typeof p1.timeMs === 'number' ? p1.timeMs : null
  const puzzleClicks =
    isPuzzleGame && typeof meta.clicks === 'number' ? meta.clicks : null

  return {
    boardToShow,
    grid,
    isPuzzleGame,
    lastMove,
    meta,
    moves,
    movesCount,
    p1Color,
    p1Name,
    p2Color,
    p2Name,
    puzzleClicks,
    puzzleTimeMs,
    score1,
    score2,
  }
}

export function getPuzzleDotStyle(): CSSProperties {
  try {
    if (typeof window !== 'undefined') {
      const accent = getComputedStyle(
        document.documentElement,
      ).getPropertyValue('--accent')
      if (accent) {
        return { background: accent.trim() }
      }
    }
  } catch {
    return { background: '#666' }
  }

  return {}
}
